import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sparkles } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const REFERRAL_OPTIONS = [
  { value: "google", label: "Google / search" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "youtube", label: "YouTube" },
  { value: "twitter", label: "X (Twitter)" },
  { value: "pinterest", label: "Pinterest" },
  { value: "store", label: "App Store / Play Store" },
  { value: "friend", label: "Friend or word of mouth" },
  { value: "blog", label: "Blog or article" },
  { value: "other", label: "Other" },
] as const;

const surveySchema = z.object({
  featureRequest: z.string().trim().min(10).max(500),
  purchaseIntent: z.string().trim().min(10).max(500),
  referralSource: z.enum(REFERRAL_OPTIONS.map((o) => o.value) as [string, ...string[]]),
  referralOther: z.string().trim().max(200),
}).refine(
  (data) =>
    data.referralSource !== "other" ||
    (data.referralOther.length >= 2 && data.referralOther.length <= 200),
  {
    message: "Please tell us where you heard about us (2–200 characters).",
    path: ["referralOther"],
  }
);

interface GenerationSurveyDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  userId: string;
  userEmail?: string | null;
  userName?: string | null;
  onSubmitted?: () => void;
}

export const GenerationSurveyDialog = ({
  open,
  onOpenChange,
  userId,
  userEmail,
  userName,
  onSubmitted,
}: GenerationSurveyDialogProps) => {
  const [featureRequest, setFeatureRequest] = useState("");
  const [purchaseIntent, setPurchaseIntent] = useState("");
  const [referralSource, setReferralSource] = useState<string>("");
  const [referralOther, setReferralOther] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const otherInputRef = useRef<HTMLInputElement>(null);
  const [touched, setTouched] = useState({
    featureRequest: false,
    purchaseIntent: false,
    referralSource: false,
    referralOther: false,
  });

  const parsed = surveySchema.safeParse({
    featureRequest,
    purchaseIntent,
    referralSource,
    referralOther,
  });
  const isValid = parsed.success;

  useEffect(() => {
    if (referralSource === "other") {
      // Small delay to allow the input to render and the layout to settle
      const timer = setTimeout(() => {
        otherInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [referralSource]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      featureRequest: true,
      purchaseIntent: true,
      referralSource: true,
      referralOther: true,
    });
    if (!isValid) {
      const issue = parsed.error?.issues?.[0]?.message || "Please complete all questions.";
      toast.error(issue);
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from("feedback_submissions")
        .insert({
          user_id: userId,
          source: "generation_gate",
          survey_feature_request: featureRequest.trim(),
          survey_purchase_intent: purchaseIntent.trim(),
          survey_referral_source: referralSource,
          survey_referral_other: referralSource === "other" ? referralOther.trim() : null,
          user_email: userEmail ?? null,
          user_name: userName ?? null,
          allow_public_display: false,
        })
        .select("id")
        .single();

      if (error) throw error;

      // Notify admin email system of new feedback submission (fire-and-forget).
      if (data?.id) {
        supabase.functions
          .invoke("notify-feedback-submission", { body: { feedbackId: data.id } })
          .catch((notifyErr) => console.warn("Feedback notification failed:", notifyErr));
      }


      toast.success("Thanks for your feedback!", {
        description: "Starting your design now…",
      });
      onSubmitted?.();
      onOpenChange?.(false);

    } catch (err) {
      const message = err instanceof Error ? err.message : "Submission failed";
      toast.error("Could not submit feedback", { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  const blockDismiss = (e: Event) => {
    e.preventDefault();
  };

  const errorFor = (
    field: "featureRequest" | "purchaseIntent" | "referralOther",
    value: string
  ) => {
    if (!touched[field]) return null;
    if (value.trim().length < 10) return "Please write at least 10 characters.";
    if (value.trim().length > 500) return "Please keep it under 500 characters.";
    return null;
  };

  const referralError =
    touched.referralSource && !referralSource
      ? "Please select where you heard about us."
      : null;

  const otherError =
    touched.referralSource &&
    referralSource === "other" &&
    (referralOther.trim().length < 2 || referralOther.trim().length > 200)
      ? "Please tell us where you heard about us (2–200 characters)."
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-lg max-h-[min(88dvh,88svh)] overflow-hidden p-0"
        onPointerDownOutside={blockDismiss}
        onInteractOutside={blockDismiss}
        onEscapeKeyDown={blockDismiss}
      >
        <form
          onSubmit={handleSubmit}
          className="flex max-h-[min(88dvh,88svh)] flex-col gap-4 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
        >
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle>Help shape what we build next</DialogTitle>
            <DialogDescription>
              A few quick answers, then your design starts right away.
            </DialogDescription>
          </DialogHeader>

          <div className="scroll-contain min-h-0 flex-1 space-y-4 overflow-y-auto px-2 pb-4">
            <div className="space-y-1.5">
              <label
                htmlFor="feature-request"
                className="text-sm font-medium leading-snug"
              >
                What's the one feature that would make Decor Canvas genuinely useful for you?{" "}
                <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="feature-request"
                value={featureRequest}
                onChange={(e) => setFeatureRequest(e.target.value)}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, featureRequest: true }))
                }
                placeholder="e.g. save my own style presets, higher-resolution downloads, furniture shopping links..."
                rows={3}
                className={cn(
                  "resize-none",
                  errorFor("featureRequest", featureRequest) && "border-destructive"
                )}
              />
              <p className="text-xs text-destructive min-h-[1.25em]">
                {errorFor("featureRequest", featureRequest)}
              </p>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="purchase-intent"
                className="text-sm font-medium leading-snug"
              >
                What would make you happy to subscribe to Decor Canvas?{" "}
                <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="purchase-intent"
                value={purchaseIntent}
                onChange={(e) => setPurchaseIntent(e.target.value)}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, purchaseIntent: true }))
                }
                placeholder="I'd pay if it was cheaper and matched my exact furniture style"
                rows={3}
                className={cn(
                  "resize-none",
                  errorFor("purchaseIntent", purchaseIntent) && "border-destructive"
                )}
              />
              <p className="text-xs text-destructive min-h-[1.25em]">
                {errorFor("purchaseIntent", purchaseIntent)}
              </p>
            </div>

            <div className="space-y-1.5">
              <span className="text-sm font-medium leading-snug">
                Where did you first hear about Decor Canvas?{" "}
                <span className="text-destructive">*</span>
              </span>
              <div className="grid grid-cols-2 gap-2">
                {REFERRAL_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={cn(
                      "flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors",
                      referralSource === option.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    )}
                  >
                    <input
                      type="radio"
                      name="referral-source"
                      value={option.value}
                      checked={referralSource === option.value}
                      onChange={() => {
                        setReferralSource(option.value);
                        setTouched((prev) => ({ ...prev, referralSource: true }));
                      }}
                      className="h-4 w-4 accent-primary"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-destructive min-h-[1.25em]">
                {referralError || otherError}
              </p>

              {referralSource === "other" && (
                <div className="pt-1">
                  <Input
                    ref={otherInputRef}
                    id="referral-other"
                    value={referralOther}
                    onChange={(e) => setReferralOther(e.target.value)}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, referralOther: true }))
                    }
                    placeholder="Tell us where — e.g. a design forum"
                    className={cn(otherError && "border-destructive")}
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="shrink-0 flex-col gap-2 sm:flex-col">
            <Button
              type="submit"
              disabled={submitting || !isValid}
              className="w-full"
              size="lg"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {submitting ? "Submitting..." : "Submit & start my design"}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Thanks for sharing — your design starts right after this.
            </p>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
